import { useState } from 'react';
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Row,
  Col,
  Button,
  Badge,
  Alert,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { StatusBadge } from '../../components/StatusBadge';
import { CopyChip } from '../../components/CopyChip';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useRefundOrder } from './useOrders';

const fmtEur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

/**
 * Drawer für Order-Detail.
 *
 * Bekommt `order` direkt aus der List-Response — Core hat (Stand 2026-06)
 * keinen Detail-Endpoint `/v1/admin/orders/:id`. Sobald der existiert,
 * kann hier `useOrderDetail(order.id)` ergänzt werden um die volle Item-
 * Liste + Acceptance-Versionen nachzuladen.
 */
export function OrderDetailDrawer({ order, isOpen, onClose }) {
  const [refundOpen, setRefundOpen] = useState(false);

  return (
    <>
      <Offcanvas isOpen={isOpen} toggle={onClose} direction="end" style={{ width: 560 }}>
        <OffcanvasHeader toggle={onClose} className="border-bottom">
          Order-Detail
        </OffcanvasHeader>
        <OffcanvasBody>
          {!order && (
            <Alert color="warning">
              Order nicht in der aktuellen Liste gefunden. Möglicherweise wurde sie
              gerade gelöscht oder du hast die Liste seitdem gefiltert.
            </Alert>
          )}
          {order && (
            <OrderDetailBody
              order={order}
              onRefund={() => setRefundOpen(true)}
            />
          )}
        </OffcanvasBody>
      </Offcanvas>

      {order && refundOpen && (
        <RefundDialog
          order={order}
          onClose={() => setRefundOpen(false)}
        />
      )}
    </>
  );
}

function OrderDetailBody({ order, onRefund }) {
  return (
    <>
      <div className="mb-4">
        <h5 className="mb-0">
          <CopyChip value={order.orderNumber} label="Order-Nr." />
        </h5>
        <div className="d-flex gap-2 align-items-center mt-2 flex-wrap">
          <StatusBadge value={order.status} />
          <Badge color="secondary" pill className="font-monospace">
            {order.provider}
          </Badge>
        </div>
      </div>

      <Row className="g-3 mb-4">
        <Detail label="Customer" value={order.userEmail ?? '—'} />
        <Detail label="Customer-ID" value={order.userId ?? '—'} />
        <Detail label="Erstellt" value={order.createdAt ? <RelativeTime iso={order.createdAt} /> : '—'} />
        <Detail label="Bezahlt" value={order.paidAt ? <RelativeTime iso={order.paidAt} /> : '—'} />
        <Detail label="AGB" value={order.acceptanceAgbVersion ?? '—'} />
        <Detail label="Datenschutz" value={order.acceptancePrivacyVersion ?? '—'} />
      </Row>

      <h6 className="text-muted text-uppercase small">Items</h6>
      {order.items.length === 0 ? (
        <p className="text-muted small">Keine Items in der Response (oder Endpoint liefert sie nicht).</p>
      ) : (
      <table className="table table-sm">
        <thead>
          <tr>
            <th>SKU</th>
            <th className="text-end">Menge</th>
            <th className="text-end">Einzelpreis</th>
            <th className="text-end">VAT</th>
            <th className="text-end">Total</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((it, i) => {
            const unit = Number(it.unitAmount ?? it.amount ?? 0);
            const qty = Number(it.quantity ?? 1);
            return (
              <tr key={i}>
                <td>
                  <code>{it.sku ?? it.name ?? `Item ${i + 1}`}</code>
                </td>
                <td className="text-end">{qty}</td>
                <td className="text-end">{Number.isFinite(unit) ? fmtEur.format(unit / 100) : '—'}</td>
                <td className="text-end">{it.vatRate != null ? `${it.vatRate}%` : '—'}</td>
                <td className="text-end">{Number.isFinite(unit * qty) ? fmtEur.format((unit * qty) / 100) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="3" />
            <td className="text-end text-muted">VAT</td>
            <td className="text-end">
              {Number.isFinite(order.vatTotal) ? fmtEur.format(order.vatTotal / 100) : '—'}
            </td>
          </tr>
          <tr>
            <td colSpan="3" />
            <td className="text-end fw-bold">Gesamt</td>
            <td className="text-end fw-bold">
              {Number.isFinite(order.total) ? fmtEur.format(order.total / 100) : '—'}
            </td>
          </tr>
        </tfoot>
      </table>
      )}

      <hr />

      <HasPermission perms={['invoices.creditnote']}>
        {(order.status === 'paid') && (
          <Button color="danger" outline onClick={onRefund}>
            💸 Refund (Credit-Note via lexware)
          </Button>
        )}
      </HasPermission>
      {order.status === 'refunded' && (
        <Alert color="info" className="mb-0 small">
          Bereits refundiert. Credit-Note in lexoffice angelegt.
        </Alert>
      )}
    </>
  );
}

function Detail({ label, value }) {
  return (
    <Col sm="6">
      <div className="text-uppercase small text-muted">{label}</div>
      <div className="fw-semibold">{value ?? '—'}</div>
    </Col>
  );
}

function RefundDialog({ order, onClose }) {
  const [reason, setReason] = useState('');
  const refund = useRefundOrder();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;
    await refund.mutateAsync({ id: order.id, reason: reason.trim() });
    onClose();
  };

  return (
    <Modal isOpen toggle={refund.isPending ? undefined : onClose}>
      <ModalHeader toggle={refund.isPending ? undefined : onClose}>
        Refund · {order.orderNumber}
      </ModalHeader>
      <form onSubmit={onSubmit}>
        <ModalBody>
          <Alert color="warning" className="small">
            Triggert Credit-Note via lexware. Refund-Betrag = vollständiger
            Order-Total ({fmtEur.format(order.total / 100)}). Partial Refunds
            sind nicht über Orbit, dafür direkt in lexware.
          </Alert>
          <FormGroup>
            <Label>Grund (für Audit-Log) *</Label>
            <Input
              type="textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z. B. service_unavailable / customer_complaint / billing_error"
              required
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose} disabled={refund.isPending}>
            Abbrechen
          </Button>
          <Button color="danger" type="submit" disabled={refund.isPending || !reason.trim()}>
            {refund.isPending ? 'Refund…' : 'Refund auslösen'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
